import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { EMPTY, Observable, expand, map, reduce } from 'rxjs';
import { RuntimeConfigService } from '../../../services/config/runtime-config.service';
import { DEFAULT_HEADERS } from '../../../utils/http.utils';
import {
  AlertDto,
  CreateDashboardRequest,
  CreateDashboardResponse,
  CreateWidgetResponse,
  Dashboard,
  DashboardDepartment,
  DashboardListItem,
  DashboardListPage,
  ResolveAlertRequest,
  TemplateLineage,
  TemplateStructure,
  UpdateDashboardRequest,
  WidgetDataResponse,
  WidgetMutationRequest,
  WidgetPreviewRequest,
  WidgetPreviewResponse,
  WidgetQueryRequest,
} from '../types/dashboard.types';

// The backend's page-size ceiling (`DashboardEndpointExtensions.ClampPageSize`).
const ALL_DASHBOARDS_PAGE_SIZE = 100;

@Service()
export class DashboardHttpService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  private get baseUrl(): string {
    const backendBaseUrl = this.config.get('backendBaseUrl') ?? '';
    return `${backendBaseUrl}/v1`;
  }

  private get dashboardsBaseUrl(): string {
    return `${this.baseUrl}/dashboards`;
  }

  private get healthUrl(): string {
    const backendBaseUrl = this.config.get('backendBaseUrl') ?? '';
    return `${backendBaseUrl}/health`;
  }

  listDashboards(
    cursor: string | null,
    pageSize: number
  ): Observable<DashboardListPage> {
    let params = new HttpParams().set('pageSize', pageSize);
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<DashboardListPage>(this.dashboardsBaseUrl, {
      headers: DEFAULT_HEADERS,
      params,
    });
  }

  listAllDashboards(): Observable<DashboardListItem[]> {
    return this.listDashboards(null, ALL_DASHBOARDS_PAGE_SIZE).pipe(
      expand(page =>
        page.nextCursor
          ? this.listDashboards(page.nextCursor, ALL_DASHBOARDS_PAGE_SIZE)
          : EMPTY
      ),
      map(page => page.items),
      reduce((all, items) => [...all, ...items], [] as DashboardListItem[])
    );
  }

  getDashboard(id: string): Observable<Dashboard> {
    return this.http.get<Dashboard>(`${this.dashboardsBaseUrl}/${id}`, {
      headers: DEFAULT_HEADERS,
    });
  }

  queryWidgetData(
    dashboardId: string,
    widgetId: string,
    request: WidgetQueryRequest
  ): Observable<WidgetDataResponse> {
    return this.http.post<WidgetDataResponse>(
      `${this.dashboardsBaseUrl}/${dashboardId}/widgets/${widgetId}/query`,
      request,
      { headers: DEFAULT_HEADERS }
    );
  }

  createDashboard(
    request: CreateDashboardRequest
  ): Observable<CreateDashboardResponse> {
    return this.http.post<CreateDashboardResponse>(
      this.dashboardsBaseUrl,
      request,
      {
        headers: DEFAULT_HEADERS,
      }
    );
  }

  updateDashboard(
    id: string,
    request: UpdateDashboardRequest
  ): Observable<void> {
    return this.http.put<void>(`${this.dashboardsBaseUrl}/${id}`, request, {
      headers: DEFAULT_HEADERS,
    });
  }

  deleteDashboard(id: string): Observable<void> {
    return this.http.delete<void>(`${this.dashboardsBaseUrl}/${id}`, {
      headers: DEFAULT_HEADERS,
    });
  }

  duplicateDashboard(id: string): Observable<CreateDashboardResponse> {
    return this.http.post<CreateDashboardResponse>(
      `${this.dashboardsBaseUrl}/${id}/duplicate`,
      null,
      { headers: DEFAULT_HEADERS }
    );
  }

  createWidget(
    dashboardId: string,
    request: WidgetMutationRequest
  ): Observable<CreateWidgetResponse> {
    return this.http.post<CreateWidgetResponse>(
      `${this.dashboardsBaseUrl}/${dashboardId}/widgets`,
      request,
      { headers: DEFAULT_HEADERS }
    );
  }

  updateWidget(
    dashboardId: string,
    widgetId: string,
    request: WidgetMutationRequest
  ): Observable<void> {
    return this.http.put<void>(
      `${this.dashboardsBaseUrl}/${dashboardId}/widgets/${widgetId}`,
      request,
      { headers: DEFAULT_HEADERS }
    );
  }

  deleteWidget(dashboardId: string, widgetId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.dashboardsBaseUrl}/${dashboardId}/widgets/${widgetId}`,
      { headers: DEFAULT_HEADERS }
    );
  }

  getTemplateLineages(): Observable<TemplateLineage[]> {
    return this.http.get<TemplateLineage[]>(
      `${this.dashboardsBaseUrl}/template-lineages`,
      { headers: DEFAULT_HEADERS }
    );
  }

  getTemplateStructure(
    lineageId: string,
    version?: number
  ): Observable<TemplateStructure> {
    let params = new HttpParams();
    if (version !== undefined) {
      params = params.set('version', version);
    }

    return this.http.get<TemplateStructure>(
      `${this.dashboardsBaseUrl}/template-lineages/${lineageId}/structure`,
      { headers: DEFAULT_HEADERS, params }
    );
  }

  getAlerts(dashboardId: string): Observable<AlertDto[]> {
    return this.http.get<AlertDto[]>(
      `${this.dashboardsBaseUrl}/${dashboardId}/alerts`,
      {
        headers: DEFAULT_HEADERS,
      }
    );
  }

  resolveAlert(
    dashboardId: string,
    alertId: string,
    request: ResolveAlertRequest
  ): Observable<void> {
    return this.http.post<void>(
      `${this.dashboardsBaseUrl}/${dashboardId}/alerts/${alertId}/resolve`,
      request,
      { headers: DEFAULT_HEADERS }
    );
  }

  getDepartments(): Observable<DashboardDepartment[]> {
    return this.http.get<DashboardDepartment[]>(
      `${this.dashboardsBaseUrl}/departments`,
      { headers: DEFAULT_HEADERS }
    );
  }

  previewWidget(
    dashboardId: string,
    request: WidgetPreviewRequest
  ): Observable<WidgetPreviewResponse> {
    return this.http.post<WidgetPreviewResponse>(
      `${this.dashboardsBaseUrl}/${dashboardId}/widgets/preview`,
      request,
      { headers: DEFAULT_HEADERS }
    );
  }

  previewWidgetLive(
    dashboardId: string,
    request: WidgetPreviewRequest
  ): Observable<WidgetPreviewResponse> {
    return this.http.post<WidgetPreviewResponse>(
      `${this.dashboardsBaseUrl}/${dashboardId}/widgets/preview-live`,
      request,
      { headers: DEFAULT_HEADERS }
    );
  }

  async isBackendAvailable(timeoutMs = 5000): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(
      () => controller.abort(),
      timeoutMs
    );

    try {
      const response = await fetch(this.healthUrl, {
        method: 'HEAD',
        cache: 'no-store',
        mode: 'cors',
        signal: controller.signal,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }
}
